import { ProColumns } from "@ant-design/pro-components";
import { Space, Avatar, Tag, Tooltip } from "antd";
import { ScheduleHistoryRow } from "../types";
import { syncStatusConfig } from "../../employees/helpers/syncStatusConfig";

type LocationOption = { value: number; label: string };
type EmployeeOption = { label: string; value: React.Key };

export const createReportColumns = (
  empList: EmployeeOption[],
  location: LocationOption[],
  firstDayOfWeek: Date,
  lastDayOfWeek: Date,
  groupByEmployee: boolean,
): ProColumns<ScheduleHistoryRow>[] => {
  // Ensure empList and location are always arrays to prevent .map() errors
  const safeEmpList = empList || [];
  const safeLocation = location || [];

  return [
    {
      disable: false,
      title: "Employee",
      dataIndex: "employee",
      hideInSearch: true,
      render(text, record) {
        if (record.isGroup) {
          return (
            <Space>
              <Avatar
                src={
                  <img
                    src="https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg"
                    alt="avatar"
                  />
                }
              />
              <div>
                {record.employee.firstName} {record.employee.lastName}
                <br />
                <Tag color="blue">{record.subRows?.length ?? 0} schedules</Tag>
              </div>
            </Space>
          );
        }
        return (
          <Space>
            <Avatar
              src={
                <img
                  src="https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg"
                  alt="avatar"
                />
              }
            />
            <div>
              {record.employee.firstName} {record.employee.lastName}
              <br />
              {record.employee.title}
            </div>
          </Space>
        );
      },
      filters: safeEmpList.map((s) => ({
        text: s.label,
        value: s.value,
      })),
      onFilter: (value, record) => record.employee.employeeID === value,
    },
    {
      disable: true,
      title: "Total Hours",
      dataIndex: "employeeActivity",
      hideInSearch: true,
      hideInTable: !groupByEmployee,
      render: (_, record) => {
        if (!record.isGroup) return null;
        const totalHours =
          record.subRows?.reduce(
            (sum, item) => sum + (item.employeeActivity?.totalHoursWorked ?? 0),
            0,
          ) ?? 0;
        return <Tag color="green">{totalHours.toFixed(2)}</Tag>;
      },
    },
    {
      disable: true,
      title: "Days Scheduled",
      dataIndex: "scheduleDate",
      hideInSearch: true,
      hideInTable: !groupByEmployee,
      render: (_, record) => {
        if (!record.isGroup) return null;
        const uniqueDays = new Set(
          record.subRows
            ?.map((item) => item.scheduleDate)
            .filter((date) => !!date),
        );
        return <Tag color="blue">{uniqueDays.size}</Tag>;
      },
    },
    {
      disable: true,
      title: "Missing Activity",
      dataIndex: "employeeActivity",
      hideInSearch: true,
      hideInTable: !groupByEmployee,
      render: (_, record) => {
        if (!record.isGroup) return null;
        const missingCount =
          record.subRows?.filter(
            (item) => !item.endTime || item.endTime === "--",
          ).length ?? 0;
        return (
          <Tag color={missingCount > 0 ? "red" : "default"}>{missingCount}</Tag>
        );
      },
    },
    {
      disable: true,
      title: "Absent",
      dataIndex: "isAbsent",
      hideInSearch: true,
      hideInTable: !groupByEmployee,
      render: (_, record) => {
        if (!record.isGroup) return null;
        const absentCount =
          record.subRows?.filter((item) => item.isAbsent === true).length ?? 0;
        return (
          <Tag color={absentCount > 0 ? "volcano" : "default"}>
            {absentCount}
          </Tag>
        );
      },
    },
    {
      disable: false,
      filters: false,
      hideInSearch: true,
      title: "Title",
      dataIndex: "title",
      hideInTable: groupByEmployee,
      render: (dom, record) =>
        record.isGroup ? null : (
          <Space size={8}>
            <span>{dom}</span>
            {record.isAbsent ? (
              <Tag color="volcano" style={{ marginInlineEnd: 0 }}>
                Absent
              </Tag>
            ) : null}
          </Space>
        ),
    },
    {
      disable: false,
      hideInSearch: true,
      title: "Notes",
      dataIndex: "notes",
      hideInTable: groupByEmployee,
      render: (dom, record) => (record.isGroup ? null : dom),
    },
    {
      disable: true,
      title: "Activity",
      dataIndex: "employeeActivity",
      search: false,
      hideInTable: groupByEmployee,
      renderFormItem: (_, { defaultRender }) => defaultRender(_),
      render: (_, record) => {
        if (record.isGroup) return null;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {record.syncStatus &&
              syncStatusConfig("TimeEntry")[record.syncStatus] && (
                <Tooltip
                  title={
                    syncStatusConfig("TimeEntry")[record.syncStatus].tooltip
                  }
                >
                  <span
                    style={{
                      marginRight: 8,
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    {syncStatusConfig("TimeEntry")[record.syncStatus].icon}
                  </span>
                </Tooltip>
              )}
            <div style={{ lineHeight: 1.4 }}>
              Clocked In: {record.startTime}
              <br />
              Clocked Out: {record.endTime ?? "--"}
            </div>
          </div>
        );
      },
    },
    {
      disable: true,
      title: "Hours Worked",
      dataIndex: "employeeActivity",
      search: false,
      hideInTable: groupByEmployee,
      renderFormItem: (_, { defaultRender }) => {
        return defaultRender(_);
      },
      render: (_, record) =>
        record.isGroup ? null : (
          <Space>
            <Tag color="green">{record.employeeActivity?.totalHoursWorked}</Tag>
          </Space>
        ),
    },
    {
      title: "Schedule Date",
      dataIndex: "scheduleDate",
      valueType: "date",
      hideInSearch: true,
      hideInTable: groupByEmployee,
      sorter: (a, b) =>
        (b.scheduleDate || "").localeCompare(a.scheduleDate || ""),
      render: (dom, record: ScheduleHistoryRow) => {
        if (record.isGroup) return null;
        const date = record.scheduleDate;
        if (!date) return null;

        const d = new Date(date);
        const options: Intl.DateTimeFormatOptions = {
          weekday: "short",
          month: "short",
          day: "numeric",
        };
        return d.toLocaleDateString(undefined, options);
      },
    },
    {
      disable: false,
      title: "Range",
      dataIndex: "scheduleDate",
      valueType: "dateRange",
      initialValue: [firstDayOfWeek, lastDayOfWeek],
      hideInTable: true,
      search: {
        transform: (value) => {
          return {
            startTime: value[0],
            endTime: value[1],
          };
        },
      },
    },
    {
      disable: false,
      title: "Employee",
      hideInForm: true,
      hideInTable: true,
      dataIndex: "selectedEmpId",
      valueType: "select",
      fieldProps: {
        options: safeEmpList,
        showSearch: true,
        allowClear: true,
      },
    },
    {
      disable: false,
      title: "Location",
      hideInForm: true,
      hideInTable: true,
      dataIndex: "selectedBranchId",
      valueType: "select",
      fieldProps: {
        options: safeLocation,
        showSearch: true,
        allowClear: true,
      },
    },
  ];
};
